import { PlayerContextState, PlayerContextTransform } from "./PlayerContext";

export type PlayerContextListener = (
  newState: PlayerContextState,
) => PlayerContextTransform | null | undefined | void;

type PlayerContextListenerTrigger = (
  state: PlayerContextState,
  oldValue?: boolean | string | number,
) => boolean | string | number;

const listeners: Record<string, PlayerContextListener> = {};
const triggerExtractors: Record<string, PlayerContextListenerTrigger> = {};
const triggerValues: Record<string, boolean | string | number> = {};

export function addContextListener(
  id: string,
  listener: PlayerContextListener,
  triggerExtractor?: PlayerContextListenerTrigger,
) {
  listeners[id] = listener;
  if (triggerExtractor != null) {
    triggerExtractors[id] = triggerExtractor;
  }
}

export function clearContextListener(id: string) {
  delete listeners[id];
  delete triggerExtractors[id];
  delete triggerValues[id];
}

export function applyContextListeners(
  newState: PlayerContextState,
): PlayerContextTransform[] {
  const validListeners = Object.keys(listeners)
    .filter((listenerKey) => {
      if (triggerExtractors?.[listenerKey] != null) {
        const oldValue = triggerValues?.[listenerKey];
        const newValue = triggerExtractors[listenerKey](newState, oldValue);
        if (triggerValues?.[listenerKey] == null) {
          triggerValues[listenerKey] = newValue;
          return false;
        }

        if (newValue == triggerValues?.[listenerKey]) {
          return false;
        }

        triggerValues[listenerKey] = newValue;
      }

      return true;
    })
    .map((listenerKey) => listeners[listenerKey](newState));

  return validListeners.filter(
    (transform) => transform != null,
  ) as PlayerContextTransform[];
}

export function applyAllTransforms(
  state: PlayerContextState,
  transform: PlayerContextTransform,
): PlayerContextState {
  let transforms = [transform];

  do {
    if (transforms.length > 0) {
      state.global.tickCount += 1;
      // This needs to be cloned because otherwise the states are the same
      state = transforms.reduce(
        (accumulatedState, currentTransform) =>
          currentTransform(accumulatedState),
        state,
      );
    }

    transforms = applyContextListeners(state);
  } while (transforms.length > 0);

  // Increment one more time to avoid cache
  state.global.tickCount += 1;

  // Return shallow clone for React to trigger context update
  return Object.assign({}, state);
}
